import { useEffect, useState } from 'react';

import { getAudioClientService } from 'services';
import { useGlobalState } from 'utils';

type UseOutputResT = {
  label: string;
  refresh: () => void;
};

export function useOutput(): UseOutputResT {
  const [, setBlockedAudioDeviceAccess] = useGlobalState(
    'ui.blockedAudioDeviceAccess',
  );

  const [res, setRes] = useState<UseOutputResT>({
    label: '',
    refresh: () => {
      void (async () => {
        try {
          const audio = await getAudioClientService();
          const { output } = await audio.getAudioDevices();

          const context = audio.getAudioContext();
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          const deviceId = ('sinkId' in context && context.sinkId) || 'default';

          const device = output.find((o) => o.deviceId === deviceId);
          const label: string = device?.label ?? '';

          setRes((prev) => ({ ...prev, label }));
        } catch (error) {
          if (error instanceof Error && error.name === 'NotAllowedError') {
            setBlockedAudioDeviceAccess(true);
          } else {
            throw error;
          }
        }
      })();
    },
  });

  // NOTE: The way we update `res`, its .refresh() method never changes after it
  // is first set.
  const { refresh } = res;

  useEffect(() => {
    refresh();

    const contextPromise = (async () => {
      const audio = await getAudioClientService();
      const context = audio.getAudioContext();
      context.addEventListener('sinkchange', refresh);
      return context;
    })();

    return () => {
      void (async () => {
        const context = await contextPromise;
        context.removeEventListener('statechange', refresh);
      })();
    };
  }, [refresh]);

  return res;
}
